import { PlusOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayout } from '@client/components/_core/layout/PageLayout';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { Space } from '@client/components/_ui/layout/Space';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_ACTION_COLUMN } from '@client/utils';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { FaqStatus, FaqVisibility } from '@shared/prisma/enums';
import { createFileRoute } from '@tanstack/react-router';
import { Select, Tag, Tooltip } from 'antd';
import type { Key } from 'antd/es/table/interface';
import { Bot, Eye, HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { FaqEdit, type FaqEditAction } from './FaqEdit';
import { FaqImport } from './FaqImport';

export const Route = createFileRoute('/admin-console/platform-content/comms-and-content/faq')({
    staticData: {
        permissions: ['admin_content:read'],
        ui: {
            icon: HelpCircle,
            title: 'FAQ configuration',
            widthVariant: 'fullWidth',
            menuDescription: 'Configure FAQ content',
        },
    },
    component: Faqs,
});

type FaqRow = RouterOutputs['admin']['faq']['listAllFaqs']['rows'][number];
type QueryParams = RouterInputs['admin']['faq']['listAllFaqs'];
type OrderBy = NonNullable<QueryParams['orderBy']>;

const columnHelper = createColumnHelper<FaqRow, OrderBy>();

// Status tag colors
const STATUS_COLORS: Record<FaqStatus, string> = {
    DRAFT: 'default',
    PUBLISHED: 'green',
    ARCHIVED: 'orange',
};

// Visibility display config
const VISIBILITY_CONFIG: Record<FaqVisibility, { label: string; icon: React.ReactNode; color: string }> = {
    PUBLIC: { label: 'Public', icon: <Eye size={14} />, color: 'blue' },
    AI_ONLY: { label: 'AI Only', icon: <Bot size={14} />, color: 'purple' },
};

function Faqs() {
    const { mutate, isPending: isDeleting } = trpc.admin.faq.deleteFaq.useMutation();
    const { mutate: deleteBatch, isPending: isDeletingBatch } = trpc.admin.faq.deleteFaqs.useMutation();
    const { isReadOnly } = useAdminModulePermissions('content');

    const [faqEditAction, setFaqEditAction] = useState<FaqEditAction>(['none']);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState<FaqStatus | undefined>(undefined);
    const [visibilityFilter, setVisibilityFilter] = useState<FaqVisibility | undefined>(undefined);
    const [selectedRowKeys, setSelectedRowKeys] = useState<bigint[]>([]);

    const [queryParams, setQueryParams] = useState<QueryParams>({
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    // Use admin route that shows all FAQs regardless of status/visibility
    const { data, isPending, refetch } = trpc.admin.faq.listAllFaqs.useQuery({
        ...queryParams,
        statusFilter,
        visibilityFilter,
    });

    const handleBatchDelete = () => {
        modal.confirm({
            title: 'Delete selected FAQs',
            content: `Are you sure you want to delete ${selectedRowKeys.length} FAQ${selectedRowKeys.length === 1 ? '' : 's'}? This action cannot be undone.`,
            okText: 'Delete',
            okButtonProps: { danger: true },
            onOk: () => {
                const hide = message.loading('Deleting FAQs...');
                deleteBatch(
                    { ids: selectedRowKeys },
                    {
                        onSuccess: (result) => {
                            hide();
                            message.success(
                                `Successfully deleted ${result.deletedCount} FAQ${result.deletedCount === 1 ? '' : 's'}`,
                            );
                            setSelectedRowKeys([]);
                            void refetch();
                        },
                        onError: (error) => {
                            hide();
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ value }) => String(value),
        }),
        columnHelper.accessor('title', {
            id: 'title',
            header: 'Title',
            sortable: 'title',
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            cell: ({ value }) => {
                const status = value as FaqStatus;
                return (
                    <Tag variant="outlined" color={STATUS_COLORS[status]}>
                        {status}
                    </Tag>
                );
            },
        }),
        columnHelper.accessor('visibility', {
            id: 'visibility',
            header: 'Visibility',
            cell: ({ value }) => {
                const visibility = value as FaqVisibility;
                const config = VISIBILITY_CONFIG[visibility];
                return (
                    <Tag variant="outlined" color={config.color} icon={config.icon}>
                        {config.label}
                    </Tag>
                );
            },
        }),
        columnHelper.display({
            id: 'categories',
            header: 'Categories',
            cell: ({ row }) => (
                <Space className="flex-wrap" size="micro">
                    {row.categories?.map((item: string) => (
                        <Tag variant="outlined" key={item} color="blue" className="m-0">
                            {item}
                        </Tag>
                    ))}
                </Space>
            ),
            className: 'max-w-xs',
        }),
        columnHelper.display({
            id: 'modules',
            header: 'Modules',
            cell: ({ row }) => (
                <Space className="flex-wrap" size="micro">
                    {row.modules?.map((item: string) => (
                        <Tag variant="outlined" key={item} color="green">
                            {item}
                        </Tag>
                    ))}
                </Space>
            ),
            className: 'max-w-xs',
        }),
        columnHelper.actions({
            id: 'actions',
            cell: ({ row }) =>
                !isReadOnly ? (
                    <Space size="small">
                        <Button.Edit onClick={() => setFaqEditAction(['edit', row.id])} />
                        <Button.Delete
                            onConfirm={(callback) =>
                                mutate(
                                    { id: row.id },
                                    {
                                        onSuccess() {
                                            callback();
                                            void refetch();
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                ) : null,
            ...DEFAULT_ACTION_COLUMN,
        }),
    ] as const;

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending || isDeleting || isDeletingBatch,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'bottom',
        },
        rowSelection: !isReadOnly
            ? {
                  enabled: true,
                  type: 'checkbox',
                  preserveSelectedRowKeys: true,
                  selectedRowKeys: selectedRowKeys as unknown as Key[],
                  onChange: (keys: Key[]) => {
                      setSelectedRowKeys(keys.map((k) => BigInt(String(k))));
                  },
              }
            : undefined,
    });

    return (
        <PageController route={Route}>
            <ReadOnlyBanner module="content" />
            <PageLayout.Header
                primaryActionAddon={
                    !isReadOnly ? (
                        <Space>
                            <Button onClick={() => setIsImportOpen(true)}>Import CSV</Button>
                            <Button icon={<PlusOutlined />} type="primary" onClick={() => setFaqEditAction(['add'])}>
                                New FAQ item
                            </Button>
                        </Space>
                    ) : undefined
                }
            />

            {/* Filters and batch actions */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex gap-4">
                    <Select
                        placeholder="Filter by status"
                        allowClear
                        value={statusFilter}
                        onChange={setStatusFilter}
                        className="w-40"
                        options={[
                            { value: FaqStatus.DRAFT, label: 'Draft' },
                            { value: FaqStatus.PUBLISHED, label: 'Published' },
                            { value: FaqStatus.ARCHIVED, label: 'Archived' },
                        ]}
                    />
                    <Select
                        placeholder="Filter by visibility"
                        allowClear
                        value={visibilityFilter}
                        onChange={setVisibilityFilter}
                        className="w-40"
                        options={[
                            { value: FaqVisibility.PUBLIC, label: 'Public' },
                            { value: FaqVisibility.AI_ONLY, label: 'AI Only' },
                        ]}
                    />
                </div>

                {/* Batch delete button */}
                {!isReadOnly && (
                    <Tooltip open={selectedRowKeys.length > 0 ? false : undefined} title="Select FAQs to delete">
                        <Button
                            type="danger"
                            disabled={selectedRowKeys.length === 0}
                            onClick={handleBatchDelete}
                            loading={isDeletingBatch}
                        >
                            Delete selected ({selectedRowKeys.length})
                        </Button>
                    </Tooltip>
                )}
            </div>

            <FaqEdit
                action={faqEditAction}
                onClose={() => {
                    setFaqEditAction(['none']);
                    void refetch();
                }}
                isReadOnly={isReadOnly}
            />

            <FaqImport
                open={isImportOpen}
                onClose={() => {
                    setIsImportOpen(false);
                    void refetch();
                }}
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick() {
                        setFaqEditAction(['edit', id]);
                    },
                })}
            />
        </PageController>
    );
}
